import axios from './axios';
import config from './config';
import history from './history';

export interface CreateROParams {
    customerId?: string;
    vehicleId?: string;
}

interface TenantSettings {
    hasSetStartingRONumber?: boolean;
    startingRONumber?: number;
}

let cachedTenantSettings: TenantSettings | null = null;
let settingsPromise: Promise<TenantSettings> | null = null;

/**
 * Gets tenant settings (cached after first call)
 */
export const getTenantSettings = async (): Promise<TenantSettings> => {
    if (cachedTenantSettings) {
        return cachedTenantSettings;
    }

    if (settingsPromise) {
        return settingsPromise;
    }

    settingsPromise = axios
        .get(`${config.apiUrl}/tenant/settings`)
        .then((res) => {
            cachedTenantSettings = res.data.tenantSettings || {};
            settingsPromise = null;
            return cachedTenantSettings!;
        })
        .catch((error) => {
            settingsPromise = null;
            console.error('Error fetching tenant settings:', error);
            return {};
        });

    return settingsPromise;
};

/**
 * Clears the cached tenant settings (call after updating settings)
 */
export const clearTenantSettingsCache = () => {
    cachedTenantSettings = null;
};

/**
 * Checks if the starting RO number modal should be shown
 */
export const shouldShowStartingNumberModal = async (): Promise<boolean> => {
    try {
        const settings = await getTenantSettings();
        
        // Check if starting number has already been set
        if (settings.hasSetStartingRONumber) {
            return false;
        }

        // Check if there are any existing repair orders
        const response = await axios.get(`${config.apiUrl}/repairOrder/board`);
        const repairOrders = response.data || [];
        
        return repairOrders.length === 0;
    } catch (error) {
        console.error('Error checking if starting number modal should show:', error);
        return false;
    }
};

/**
 * Updates the starting RO number in tenant settings
 */
export const updateStartingRONumber = async (startingNumber: number): Promise<void> => {
    const settings = await getTenantSettings();
    await axios.put(`${config.apiUrl}/tenant`, {
        ...settings,
        startingRONumber: startingNumber,
        hasSetStartingRONumber: true,
    });
    clearTenantSettingsCache();
};

/**
 * Creates a new repair order
 */
export const createRepairOrder = async (params: CreateROParams = {}): Promise<string | null> => {
    try {
        const response = await axios.post(`${config.apiUrl}/repairOrder`, params);
        return response.data?.id || null;
    } catch (error) {
        console.error('Error creating repair order:', error);
        return null;
    }
};

/**
 * Creates a repair order and navigates to it
 * Returns true if navigation happened, false if modal should be shown
 */
export const createAndNavigateToRepairOrder = async (
    params: CreateROParams = {},
    onShowModal?: () => void
): Promise<boolean> => {
    const shouldShowModal = await shouldShowStartingNumberModal();
    
    if (shouldShowModal && onShowModal) {
        onShowModal();
        return false;
    }

    const roId = await createRepairOrder(params);
    if (roId) {
        history.push(`/repairOrder/${roId}`);
        return true;
    }
    
    return false;
};
